/**
 * WorkerJobDetails.js - Full job detail modal/page
 * Shows complete job information before worker picks it up
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, MapPin, Clock, DollarSign, Calendar, User, Phone, FileText,
  Shirt, Truck, Car, Home, ShoppingBag, AlertCircle, Navigation,
  CheckCircle, Package, Scale, Thermometer
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const serviceIcons = {
  cleaning: Home,
  laundry: Shirt,
  courier: Truck,
  rides: Car,
  errands: ShoppingBag,
  tasks: ShoppingBag
};

const serviceColors = {
  cleaning: 'from-blue-500 to-blue-600',
  laundry: 'from-purple-500 to-purple-600',
  courier: 'from-orange-500 to-orange-600',
  rides: 'from-green-500 to-green-600',
  errands: 'from-pink-500 to-pink-600',
  tasks: 'from-pink-500 to-pink-600'
};

const WorkerJobDetails = ({ job, isOpen, onClose, onPickup, isLoading = false }) => {
  const [pickingUp, setPickingUp] = useState(false);
  
  if (!isOpen || !job) return null;
  
  const ServiceIcon = serviceIcons[job.service_type] || ShoppingBag;
  const gradientColor = serviceColors[job.service_type] || 'from-gray-500 to-gray-600';
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };
  
  const handlePickup = async () => {
    setPickingUp(true);
    try {
      await onPickup?.(job);
    } finally {
      setPickingUp(false);
    }
  };
  
  const isUrgent = job.urgency_level === 'urgent' || job.is_urgent;
  const workerPay = job.worker_pay || job.display_pay || 0;
  const urgentBonus = job.urgent_fee_amount || 0;
  const totalPay = workerPay + urgentBonus;
  
  // Service-specific details
  const courierInfo = job.courier_info || {};
  const cleaningInfo = job.cleaning_info || {};
  const laundryInfo = job.laundry_info || {};
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-[var(--background)] w-full max-w-lg max-h-[90vh] overflow-hidden rounded-t-3xl sm:rounded-3xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`bg-gradient-to-r ${gradientColor} p-6 relative`}>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                <ServiceIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-white/80 text-sm uppercase tracking-wide">
                  {job.service_type?.replace('_', ' ')}
                </p>
                <h2 className="text-xl font-bold text-white">
                  {job.service_name || 'Job Details'}
                </h2>
                {isUrgent && (
                  <div className="inline-flex items-center gap-1 bg-red-500 px-2 py-0.5 rounded-full mt-1">
                    <AlertCircle className="w-3 h-3 text-white" />
                    <span className="text-xs font-bold text-white">URGENT</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh] space-y-6">
            {/* Pay Section - Most Important */}
            <div className="bg-[var(--primary)]/10 border border-[var(--primary)]/30 rounded-xl p-4">
              <p className="text-sm text-[var(--text-muted)] mb-1">Your Pay</p>
              <div className="flex items-end justify-between">
                <div>
                  <span className="text-3xl font-bold text-[var(--primary)]">
                    ${totalPay.toFixed(2)}
                  </span>
                  {urgentBonus > 0 && (
                    <p className="text-sm text-green-500 mt-1">
                      Includes ${urgentBonus.toFixed(2)} urgent bonus
                    </p>
                  )}
                </div>
                <div className="text-right text-sm text-[var(--text-muted)]">
                  <p>Base: ${workerPay.toFixed(2)}</p>
                  {urgentBonus > 0 && <p>Bonus: +${urgentBonus.toFixed(2)}</p>}
                </div>
              </div>
            </div>
            
            {/* Date & Time */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-[var(--background-subtle)] rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[var(--primary)]" />
              </div>
              <div>
                <p className="font-semibold text-[var(--text-primary)]">
                  {formatDate(job.scheduled_date)}
                </p>
                <p className="text-[var(--text-muted)]">
                  {formatTime(job.scheduled_time)}
                </p>
              </div>
            </div>
            
            {/* Location */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-[var(--background-subtle)] rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[var(--primary)]" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-[var(--text-primary)]">
                  {job.address || 'Address provided after pickup'}
                </p>
                <p className="text-[var(--text-muted)]">
                  {job.city}, {job.state || 'NC'} {job.zip_code}
                </p>
                {job.distance_miles && (
                  <p className="text-sm text-[var(--text-muted)] mt-1">
                    {job.distance_miles.toFixed(1)} miles from service area center
                  </p>
                )}
              </div>
            </div>
            
            {/* Duration */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-[var(--background-subtle)] rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-[var(--primary)]" />
              </div>
              <div>
                <p className="font-semibold text-[var(--text-primary)]">
                  Estimated Duration
                </p>
                <p className="text-[var(--text-muted)]">
                  {job.duration_hours ? `${job.duration_hours} hour${job.duration_hours > 1 ? 's' : ''}` : 'To be determined'}
                </p>
              </div>
            </div>
            
            {/* Customer (limited info) */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-[var(--background-subtle)] rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-[var(--primary)]" />
              </div>
              <div>
                <p className="font-semibold text-[var(--text-primary)]">
                  {job.client_name || job.customer_name || 'Customer'}
                </p>
                <p className="text-sm text-[var(--text-muted)]">
                  Contact info available after pickup
                </p>
              </div>
            </div>
            
            {/* Service-Specific Details */}
            {job.service_type === 'courier' && courierInfo && (
              <div className="bg-[var(--background-subtle)] rounded-xl p-4 space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <Package className="w-4 h-4 text-[var(--primary)]" />
                  Courier Details
                </h4>
                {courierInfo.pickup_address && (
                  <div className="text-sm">
                    <p className="text-[var(--text-muted)]">Pickup From:</p>
                    <p className="text-[var(--text-primary)]">
                      {courierInfo.pickup_city}, {courierInfo.pickup_state || 'NC'}
                    </p>
                  </div>
                )}
                {courierInfo.package_description && (
                  <div className="text-sm">
                    <p className="text-[var(--text-muted)]">Package:</p>
                    <p className="text-[var(--text-primary)]">{courierInfo.package_description}</p>
                  </div>
                )}
                {courierInfo.is_fragile && (
                  <div className="flex items-center gap-2 text-amber-500">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Fragile - Handle with care</span>
                  </div>
                )}
              </div>
            )}
            
            {job.service_type === 'cleaning' && cleaningInfo && (
              <div className="bg-[var(--background-subtle)] rounded-xl p-4 space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <Home className="w-4 h-4 text-[var(--primary)]" />
                  Cleaning Details
                </h4>
                {cleaningInfo.home_size && (
                  <p className="text-sm"><span className="text-[var(--text-muted)]">Home Size:</span> {cleaningInfo.home_size}</p>
                )}
                {cleaningInfo.bedrooms && (
                  <p className="text-sm"><span className="text-[var(--text-muted)]">Bedrooms:</span> {cleaningInfo.bedrooms}</p>
                )}
                {cleaningInfo.bathrooms && (
                  <p className="text-sm"><span className="text-[var(--text-muted)]">Bathrooms:</span> {cleaningInfo.bathrooms}</p>
                )}
              </div>
            )}
            
            {job.service_type === 'laundry' && laundryInfo && (
              <div className="bg-[var(--background-subtle)] rounded-xl p-4 space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <Shirt className="w-4 h-4 text-[var(--primary)]" />
                  Laundry Details
                </h4>
                {laundryInfo.estimated_weight && (
                  <p className="text-sm"><span className="text-[var(--text-muted)]">Est. Weight:</span> {laundryInfo.estimated_weight} lbs</p>
                )}
                {laundryInfo.service_type && (
                  <p className="text-sm"><span className="text-[var(--text-muted)]">Service:</span> {laundryInfo.service_type}</p>
                )}
              </div>
            )}
            
            {/* Special Instructions */}
            {job.special_instructions && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <h4 className="font-semibold flex items-center gap-2 text-amber-500 mb-2">
                  <FileText className="w-4 h-4" />
                  Special Instructions
                </h4>
                <p className="text-sm text-[var(--text-primary)]">
                  {job.special_instructions}
                </p>
              </div>
            )}
            
            {/* Claim Status Info */}
            {!job.can_claim && !job.already_claimed_by_me && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <p className="text-sm text-red-400">
                  {job.schedule_conflict 
                    ? "This job conflicts with your existing schedule."
                    : job.weekly_hours_exceeded
                      ? "Picking up this job would exceed your weekly hour cap."
                      : "This job is not available for pickup at this time."}
                </p>
              </div>
            )}
          </div>
          
          {/* Footer - Action Button */}
          <div className="p-4 border-t border-[var(--border-primary)] bg-[var(--background-paper)]">
            {job.already_claimed_by_me ? (
              <div className="flex items-center justify-center gap-2 py-3 bg-green-500/20 text-green-500 rounded-xl">
                <CheckCircle className="w-5 h-5" />
                <span className="font-bold">You've Claimed This Job</span>
              </div>
            ) : (
              <Button
                onClick={handlePickup}
                disabled={!job.can_claim || pickingUp || isLoading}
                className="w-full h-14 text-lg font-bold bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-black rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pickup-job-confirm"
              >
                {pickingUp || isLoading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Processing...
                  </span>
                ) : (
                  'PICK UP THIS JOB'
                )}
              </Button>
            )}
            
            {job.can_claim && !job.already_claimed_by_me && (
              <p className="text-center text-xs text-[var(--text-muted)] mt-2">
                By picking up this job, you commit to completing it on time.
              </p>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WorkerJobDetails;
